/*
 * RouteStore
 *
 * Two layers:
 *   1. Pure reducer: `(state, action) -> newState`. Plain JS, no React.
 *   2. React glue: a context provider that holds the reduced state alive
 *      across renders, plus a hook that lets any component read it and
 *      dispatch actions.
 *
 * Components below the provider can call `useRouteStore()` to get
 * `{ state, replaceRoute }`. They don't need to know about React context
 * or useReducer — those are implementation details of this file.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
} from "react"

import {
  childIdsOf,
  createEmptyNormalizedState,
  isActiveEntity,
  isPlannedEntity,
  mergeNormalizedStates,
  removeRoute,
  removeVoyage,
  setRouteRelationForVoyage,
  setVesselIds,
  setVoyageRelationForVessel,
} from "./RouteState.jsx"
import {
  selectDraftRoutes,
  selectRenderableRoute,
} from "./RouteSelectors.jsx"

// ---------------------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------------------
//
// A draft is an unsaved route living only in this store, under a negative
// synthetic route id (DB ids are positive). Which vessel a draft belongs to
// is data on the entity (vesselId/draftContext), never encoded in the id.

export function isDraftRouteId(routeId) {
  const id = Number(routeId)
  return Number.isFinite(id) && id < 0
}

// Tree-selection ids for drafts, mixed with numeric DB ids in one list.
const DRAFT_SELECTION_PREFIX = "draft:route:"

export function draftSelectionId(routeId) {
  return `${DRAFT_SELECTION_PREFIX}${Number(routeId)}`
}

export function isDraftSelectionId(selectionId) {
  return (
    typeof selectionId === "string" &&
    selectionId.startsWith(DRAFT_SELECTION_PREFIX)
  )
}

export function draftRouteIdFromSelectionId(selectionId) {
  if (!isDraftSelectionId(selectionId)) return null
  const id = Number(selectionId.slice(DRAFT_SELECTION_PREFIX.length))
  return Number.isFinite(id) ? id : null
}

// Tree-selection id for a vessel's reported track. There is no route entity
// behind it; numeric-id consumers (waypoint loader, selectors) skip it.
const REPORTED_SELECTION_PREFIX = "reported:vessel:"

export function reportedSelectionId(vesselId) {
  return `${REPORTED_SELECTION_PREFIX}${Number(vesselId)}`
}

export function isReportedSelectionId(selectionId) {
  return (
    typeof selectionId === "string" &&
    selectionId.startsWith(REPORTED_SELECTION_PREFIX)
  )
}

// Selection ids that need no vessel/voyage parents when toggled.
export function isParentlessSelectionId(selectionId) {
  return isDraftSelectionId(selectionId) || isReportedSelectionId(selectionId)
}

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------
//
// An "action" is just a plain object. By convention it has a `type` (a string
// describing what happened) and a `payload` (the data needed to apply it).
// We collect the type strings here so callers can `import { ROUTE_ACTIONS }`
// and never typo a string.

export const ROUTE_ACTIONS = Object.freeze({
  // The whole route was replaced — used after a successful pathfinder /
  // planroute-analyser response. Payload: { routeId, normalizedFragment }.
  ROUTE_REPLACED: "route/REPLACED",

  // A fetched batch of DB routes replaces every route contained in the
  // fragment (waypoints per route dropped first, entities merged).
  // Payload: { normalizedFragment }.
  ROUTES_REPLACED: "routes/REPLACED",

  // Additive merge of tree metadata (single upserts, import hint).
  // Payload: { normalizedFragment } from normalizeTreeMetadata.
  ENTITIES_UPSERTED: "entities/UPSERTED",

  // GET /vessels is authoritative: entities merge, relations.vesselIds is
  // replaced with exactly the fetched ids. Payload: { normalizedFragment }.
  VESSELS_SET: "vessels/SET",

  // Authoritative child list per parent (set semantics — a smaller pagination
  // window shrinks the list, server-side deletions drop out). Entities merge;
  // waypoints stay untouched. Payload: { vesselId|voyageId, normalizedFragment }.
  VOYAGES_SET_FOR_VESSEL: "voyages/SET_FOR_VESSEL",
  ROUTES_SET_FOR_VOYAGE: "routes/SET_FOR_VOYAGE",

  // The only delete paths (merge never deletes). Payload: { routeId } / { voyageId }.
  ROUTE_REMOVED: "route/REMOVED",
  VOYAGE_REMOVED: "voyage/REMOVED",

  // Local mirrors of the DB lifecycle cascades, applied atomically in the
  // reducer (no stale setState-return round-trips).
  ROUTE_SIBLINGS_ARCHIVED: "route/SIBLINGS_ARCHIVED",
  VOYAGE_SIBLINGS_ARCHIVED: "voyage/SIBLINGS_ARCHIVED",
  VOYAGE_ROUTES_ARCHIVED: "voyage/ROUTES_ARCHIVED",
})

// ---------------------------------------------------------------------------
// The reducer
// ---------------------------------------------------------------------------
//
// Signature: (state, action) -> newState
//
// Rules:
//   1. NEVER mutate `state`. Always return a new object (or return `state`
//      unchanged if the action doesn't apply).
//   2. The function is pure: no fetch, no console.log of important things,
//      no Date.now() based on real time, etc.
//   3. Unknown action types must return `state` unchanged.
//
// This file does NOT import anything from React. It would work in plain
// Node.js. That's a deliberate property — the reducer is testable in
// isolation.

function toId(value) {
  const id = Number(value)
  return Number.isFinite(id) ? id : null
}

function archiveEntities(state, entityKey, ids, { keepId = null, predicate }) {
  const entitiesById = { ...state.entities[entityKey] }
  let changed = false
  for (const id of ids) {
    if (id == null || id === keepId) continue
    const entity = entitiesById[id]
    if (!entity || !predicate(entity)) continue
    entitiesById[id] = { ...entity, state: "archived", active: false }
    changed = true
  }
  if (!changed) return state
  return { ...state, entities: { ...state.entities, [entityKey]: entitiesById } }
}

export function reducer(state, action) {
  switch (action?.type) {
    case ROUTE_ACTIONS.ROUTE_REPLACED: {
      const { routeId, normalizedFragment } = action.payload ?? {}
      const targetId = toId(routeId)
      if (targetId == null || !normalizedFragment) return state

      // mergeNormalizedStates is additive (it never deletes). For
      // ROUTE_REPLACED we want the response to be the sole truth for this
      // routeId, so we first drop any existing waypoints for this route, then
      // merge the new fragment on top.
      const cleared = {
        ...state,
        waypointsByRouteId: { ...state.waypointsByRouteId },
      }
      delete cleared.waypointsByRouteId[targetId]

      return mergeNormalizedStates(cleared, normalizedFragment)
    }

    case ROUTE_ACTIONS.ROUTES_REPLACED: {
      const { normalizedFragment } = action.payload ?? {}
      if (!normalizedFragment) return state

      // Same replace semantics as ROUTE_REPLACED, for every route carried in
      // the fragment — a refetch must not leave stale waypoints behind.
      const cleared = {
        ...state,
        waypointsByRouteId: { ...state.waypointsByRouteId },
      }
      for (const routeId of Object.keys(
        normalizedFragment.waypointsByRouteId ?? {},
      )) {
        delete cleared.waypointsByRouteId[routeId]
      }

      return mergeNormalizedStates(cleared, normalizedFragment)
    }

    case ROUTE_ACTIONS.ENTITIES_UPSERTED: {
      const { normalizedFragment } = action.payload ?? {}
      if (!normalizedFragment) return state
      return mergeNormalizedStates(state, normalizedFragment)
    }

    case ROUTE_ACTIONS.VESSELS_SET: {
      const { normalizedFragment } = action.payload ?? {}
      if (!normalizedFragment) return state
      const merged = mergeNormalizedStates(state, normalizedFragment)
      return setVesselIds(
        merged,
        Object.keys(normalizedFragment.entities?.vesselsById ?? {}),
      )
    }

    case ROUTE_ACTIONS.VOYAGES_SET_FOR_VESSEL: {
      const { vesselId, normalizedFragment } = action.payload ?? {}
      const targetId = toId(vesselId)
      if (targetId == null || !normalizedFragment) return state
      const merged = mergeNormalizedStates(state, normalizedFragment)
      const voyageIds = Object.values(
        normalizedFragment.entities?.voyagesById ?? {},
      )
        .filter((voyage) => toId(voyage?.vesselId) === targetId)
        .map((voyage) => voyage.id)
      return setVoyageRelationForVessel(merged, targetId, voyageIds)
    }

    case ROUTE_ACTIONS.ROUTES_SET_FOR_VOYAGE: {
      const { voyageId, normalizedFragment } = action.payload ?? {}
      const targetId = toId(voyageId)
      if (targetId == null || !normalizedFragment) return state
      const merged = mergeNormalizedStates(state, normalizedFragment)
      const routeIds = Object.values(
        normalizedFragment.entities?.routesById ?? {},
      )
        .filter((route) => toId(route?.voyageId) === targetId)
        .map((route) => route.id)
      return setRouteRelationForVoyage(merged, targetId, routeIds)
    }

    case ROUTE_ACTIONS.ROUTE_REMOVED: {
      const { routeId } = action.payload ?? {}
      return removeRoute(state, routeId)
    }

    case ROUTE_ACTIONS.VOYAGE_REMOVED: {
      const { voyageId } = action.payload ?? {}
      return removeVoyage(state, voyageId)
    }

    case ROUTE_ACTIONS.ROUTE_SIBLINGS_ARCHIVED: {
      const { voyageId, keepRouteId } = action.payload ?? {}
      const targetId = toId(voyageId)
      if (targetId == null) return state
      const ids = childIdsOf(
        state.relations?.routeIdsByVoyageId?.[targetId],
        state.entities.routesById,
        "voyageId",
        targetId,
      )
      return archiveEntities(state, "routesById", ids, {
        keepId: toId(keepRouteId),
        predicate: (route) => isPlannedEntity(route) && isActiveEntity(route),
      })
    }

    case ROUTE_ACTIONS.VOYAGE_SIBLINGS_ARCHIVED: {
      const { vesselId, keepVoyageId } = action.payload ?? {}
      const targetId = toId(vesselId)
      if (targetId == null) return state
      const ids = childIdsOf(
        state.relations?.voyageIdsByVesselId?.[targetId],
        state.entities.voyagesById,
        "vesselId",
        targetId,
      )
      const keepId = toId(keepVoyageId)
      const demoted = [...ids].filter((id) => {
        const voyage = state.entities.voyagesById?.[id]
        return id !== keepId && isPlannedEntity(voyage) && isActiveEntity(voyage)
      })
      // Mirrors the DB trigger: an archived voyage takes its planned routes along.
      return demoted.reduce(
        (next, voyageId) => reducer(next, { type: ROUTE_ACTIONS.VOYAGE_ROUTES_ARCHIVED, payload: { voyageId } }),
        archiveEntities(state, "voyagesById", demoted, {
          predicate: () => true,
        }),
      )
    }

    case ROUTE_ACTIONS.VOYAGE_ROUTES_ARCHIVED: {
      const { voyageId } = action.payload ?? {}
      const targetId = toId(voyageId)
      if (targetId == null) return state
      const ids = childIdsOf(
        state.relations?.routeIdsByVoyageId?.[targetId],
        state.entities.routesById,
        "voyageId",
        targetId,
      )
      return archiveEntities(state, "routesById", ids, {
        predicate: (route) =>
          isPlannedEntity(route) && route?.state !== "archived",
      })
    }

    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Initial state factory
// ---------------------------------------------------------------------------
//
// We delegate to RouteState.jsx so the empty shape stays consistent with the
// rest of the app.

export function createInitialState() {
  return createEmptyNormalizedState()
}

// ---------------------------------------------------------------------------
// React glue — context + provider + hook
// ---------------------------------------------------------------------------
//
// This is where React enters the picture. The pieces are:
//
//   - `RouteStoreContext`: a React context object. Think of it as a typed
//     "tunnel" that lets a value flow from a <Provider> down to any
//     component that wants to read it, no matter how deep.
//
//   - `RouteStoreProvider`: a component that calls `useReducer` once and
//     puts the resulting `{ state, dispatch }` (plus action helpers) into
//     the context. Wrap it around the part of the app that needs access.
//
//   - `useRouteStore`: a hook (a function that calls other hooks). Any
//     component can call it to read `state` or fire actions. It throws if
//     the component is rendered outside the provider — that's a hard error
//     because a missing provider would mean "silently no state."

const RouteStoreContext = createContext(null)

export function RouteStoreProvider({ children }) {
  // useReducer is the React hook that pairs our reducer with state. On every
  // render, `state` is the latest reduced state, and `dispatch` is a stable
  // function that, when called with an action, runs the reducer and triggers
  // a re-render of consumers if the new state is a new object.
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState)

  // Action creators. Plain functions that wrap `dispatch` with the right
  // action shape, so callers don't have to remember string types or payload
  // structure. They're memoised so consumers that only depend on the actions
  // (not on state) don't re-render every time state changes.
  const actions = useMemo(
    () => ({
      replaceRoute: (routeId, normalizedFragment) =>
        dispatch({
          type: ROUTE_ACTIONS.ROUTE_REPLACED,
          payload: { routeId, normalizedFragment },
        }),
      replaceRoutes: (normalizedFragment) =>
        dispatch({
          type: ROUTE_ACTIONS.ROUTES_REPLACED,
          payload: { normalizedFragment },
        }),
      upsertEntities: (normalizedFragment) =>
        dispatch({
          type: ROUTE_ACTIONS.ENTITIES_UPSERTED,
          payload: { normalizedFragment },
        }),
      setVessels: (normalizedFragment) =>
        dispatch({
          type: ROUTE_ACTIONS.VESSELS_SET,
          payload: { normalizedFragment },
        }),
      setVoyagesForVessel: (vesselId, normalizedFragment) =>
        dispatch({
          type: ROUTE_ACTIONS.VOYAGES_SET_FOR_VESSEL,
          payload: { vesselId, normalizedFragment },
        }),
      setRoutesForVoyage: (voyageId, normalizedFragment) =>
        dispatch({
          type: ROUTE_ACTIONS.ROUTES_SET_FOR_VOYAGE,
          payload: { voyageId, normalizedFragment },
        }),
      removeRoute: (routeId) =>
        dispatch({
          type: ROUTE_ACTIONS.ROUTE_REMOVED,
          payload: { routeId },
        }),
      removeVoyage: (voyageId) =>
        dispatch({
          type: ROUTE_ACTIONS.VOYAGE_REMOVED,
          payload: { voyageId },
        }),
      archiveRouteSiblings: (voyageId, keepRouteId) =>
        dispatch({
          type: ROUTE_ACTIONS.ROUTE_SIBLINGS_ARCHIVED,
          payload: { voyageId, keepRouteId },
        }),
      archiveVoyageSiblings: (vesselId, keepVoyageId) =>
        dispatch({
          type: ROUTE_ACTIONS.VOYAGE_SIBLINGS_ARCHIVED,
          payload: { vesselId, keepVoyageId },
        }),
      archiveVoyageRoutes: (voyageId) =>
        dispatch({
          type: ROUTE_ACTIONS.VOYAGE_ROUTES_ARCHIVED,
          payload: { voyageId },
        }),
    }),
    [],
  )

  // Separate UI-only flag for "is a recalc in flight?" — kept outside the
  // reducer because nothing derives from it; it's a transient signal that
  // gates edit handlers and powers visual feedback.
  const [isCalculating, setIsCalculating] = useState(false)
  const stableSetIsCalculating = useCallback((v) => setIsCalculating(v), [])

  // The value put into the context. Memoised on (state, isCalculating) so
  // consumers that read either get a new reference when it changes.
  const value = useMemo(
    () => ({
      state,
      isCalculating,
      setIsCalculating: stableSetIsCalculating,
      ...actions,
    }),
    [state, isCalculating, stableSetIsCalculating, actions],
  )

  return (
    <RouteStoreContext.Provider value={value}>
      {children}
    </RouteStoreContext.Provider>
  )
}

export function useRouteStore() {
  const ctx = useContext(RouteStoreContext)
  if (!ctx) {
    throw new Error(
      "useRouteStore must be used inside <RouteStoreProvider>. " +
        "Did you forget to mount the provider in Application.jsx?",
    )
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Selector hooks
// ---------------------------------------------------------------------------
//
// Selectors are functions that derive a view of state without storing extra
// data. The hooks below wrap them so React components can subscribe to a
// derived value and re-render only when it changes.
//
// `useRenderableRoute(routeId)` returns the same `{ jsonRequest, summary,
// points, lines }` shape consumers expect, computed live
// from the store. Memoised on `state` + `routeId` so the result reference
// only changes when the underlying data changes.

export function useRenderableRoute(routeId) {
  const { state } = useRouteStore()
  return useMemo(
    () => selectRenderableRoute(state, routeId),
    [state, routeId],
  )
}

// All live drafts (unsaved routes with waypoints) as renderables.
export function useDraftRoutes() {
  const { state } = useRouteStore()
  return useMemo(() => selectDraftRoutes(state), [state])
}

// `true` while a recalc is in flight. Edit handlers should bail when this
// is set to avoid racing requests; visual layers should reduce opacity to
// signal "busy" to the user.
export function useIsCalculating() {
  return useRouteStore().isCalculating
}
